import type { Product, ProductVariation } from "@/lib/products";
import {
  applyGroupDiscount,
  DISKON_SLOT_KEY,
  DISKON_SLOTS,
  isDiskonSlot,
  type CustomerGroup
} from "@/lib/reseller-groups";
import {
  applyPromo,
  discountBadgeText,
  pickCheapestPromo,
  promoDiscountForSize,
  promoLevelPolicy,
  promosForLevel,
  type PriceCut,
  type ProductPromo
} from "@/lib/promo";
import { variantSizeLabel } from "@/lib/sizes";
import { levelBrandPersen, levelForProduct } from "@/lib/groupBrandDiscount";

export type { PriceCut } from "@/lib/promo";

// CATATAN ARSITEKTUR HARGA — model tunggal: GRUP % + OVERRIDE PER-VARIAN.
//   0. PROMO KAMPANYE (mig 3470). Bila ada kampanye promo yang berlaku untuk
//      produk ini, cara bergabungnya ditentukan `level_policy` kampanye
//      (docs/PUTUSAN-PROMO.md):
//        • `replace` (bawaan, seluruh kampanye lama): harga promo dipakai untuk
//          SEMUA pembeli — override per-varian DAN diskon grup di bawah
//          DILEWATI seluruhnya ("promo menang mutlak", keputusan pemilik).
//        • `stack` ("Diskon harga coret", 2026-09-16): potongan promo dipotong
//          dari price_normal lebih dulu → "harga coret" C, LALU tangga §2–§3
//          dihitung dari C. Contoh: 100.000 − 10 % = 90.000, Distributor 30 %
//          → 63.000. Kedua potongan dilaporkan di `cuts` (badge "🔥 10% + 30%").
//      Beberapa kampanye sekaligus → yang TERMURAH pada harga AKHIR pembeli
//      (`pickCheapestPromo` + `finalPriceOf`), bukan pada harga promonya.
//   1. Harga dasar = price_normal varian (fallback price_normal produk).
//   1a. DISKON MEREK PER LEVEL (mig 7070): level pembeli punya aturan untuk
//      merek produk ini → harga dasar × (1 − persen) dan §2–§3 DILEWATI
//      (keputusan pemilik 2026-10-08: "aturan merek menang" atas diskon
//      per-produk). Aturan sampai ke sini HANYA lewat `levelForProduct`
//      (groupBrandDiscount.ts) yang mengisi `group.brandPersen` per produk;
//      fungsi yang memegang produk (`getProductPriceView`/`getProductPrice`)
//      memanggilnya sendiri. Promo §0 memperlakukannya persis seperti diskon
//      level: `replace` menang, `stack` dihitung dari harga coret.
//   2. Jika varian punya diskon khusus untuk grup customer, override itu menang:
//        grup slot 1 → product_variants.diskon_reseller
//        grup slot 2 → product_variants.diskon_agen
//        grup slot 3..15 → product_variants.diskon_slot3 … diskon_slot15 (mig 5600/7300)
//      (mode persen/nominal dari diskon_mode). Berlaku untuk semua slot aktif.
//      Petanya SATU — `DISKON_SLOT_KEY` di reseller-groups.ts (mig 5600) — dipakai
//      juga oleh prefill nota, export PO, dan pratinjau form produk.
//   3. Tanpa override → pakai diskon grup (reseller_groups.discount_persen).
//   4. Tanpa grup (Customer biasa) → harga normal, tanpa diskon.
// Kolom legacy products/product_variants.price_reseller/price_agen TIDAK dipakai.

type VariantOverride = { mode: "persen" | "nominal"; value: number };

/**
 * TIER = potongan yang MEMANG dipilih model harga di atas untuk sebuah varian
 * (promo kampanye / override per-varian / diskon grup), dinyatakan sebagai
 * mode + besaran: persen (dari harga dasar) atau nominal (Rp per pcs). `null`
 * = harga dasar dibayar penuh.
 *
 * Dibutuhkan VOUCHER (2026-09): keputusan pemilik menetapkan urutan
 * "harga jual umum − voucher, BARU diskon level", jadi jalur voucher harus
 * tahu potongan MANA yang berlaku untuk menerapkannya ulang di atas harga
 * yang sudah dipotong voucher (`unitPriceAfterVoucher`, voucher.ts). Diturunkan
 * di SATU tempat (`explainVariantPrice`) yang sekaligus menjadi badan
 * `getVariantPrice`, sehingga angka dan penjelasannya mustahil menyimpang.
 */
export type PriceTier = { mode: "persen" | "nominal"; value: number } | null;

/**
 * Harga varian BERIKUT penjelasannya — keluaran `explainVariantPrice`.
 *
 *   • `cuts`  — seluruh potongan yang membentuk `price`, URUT penerapan
 *               (promo dulu, lalu level). Kosong = harga dasar penuh.
 *   • `tier`  — potongan PERTAMA (= `cuts[0]`), kontrak voucher lama.
 *   • `tier2` — potongan KEDUA (= `cuts[1]`), hanya kampanye `stack` + level
 *               (mesin voucher dua tier, mig voucher_dua_tier). `null` selain itu.
 *   • `strikePrice` — angka yang DICORET di layar = harga sebelum potongan
 *               TERAKHIR (keputusan pemilik 2026-09-16): tamu / satu potongan →
 *               price_normal; reseller pada promo coret → harga coret C.
 *               BUKAN pengganti `normalPrice` (harga umum DB) di jalur uang.
 */
export type VariantPriceExplanation = {
  price: number | undefined;
  tier: PriceTier;
  tier2: PriceTier;
  cuts: readonly PriceCut[];
  strikePrice: number | undefined;
  promo: ProductPromo | null;
  /**
   * HARGA TAMBAHAN varian (mig 7900) yang sudah TERMASUK di `price` dan
   * `strikePrice`, ditempel SESUDAH seluruh potongan: dasar − diskon + tambahan.
   * `cuts`/`tier` tetap dihitung atas DASAR (`price_normal`), dan `normalPrice`
   * di jalur uang tetap dasar — pembaca yang ingin "harga umum tampil" menjumlah
   * keduanya. 0 = tanpa tambahan. Seluruh putusannya: docs/PUTUSAN-HARGA-TAMBAHAN.md.
   */
  priceExtra: number;
};

/**
 * Harga tambahan sebuah varian untuk rumus: angka ≥ 0 yang terbaca; absen /
 * NULL / tak sah = 0. SATU penilai untuk mesin harga, potret pesanan, dan form.
 */
export function variantPriceExtra(
  variant: Pick<ProductVariation, "price_extra"> | null | undefined
): number {
  const v = variant?.price_extra;
  return v !== undefined && v !== null && Number.isFinite(v) && v > 0 ? v : 0;
}

/** `PriceTier` dari sebuah potongan (bentuk kontrak voucher: mode + nilai). */
function tierOf(cut: PriceCut | null | undefined): PriceTier {
  return cut ? { mode: cut.mode, value: cut.value } : null;
}

/**
 * Diskon khusus varian utk grup ini (slot 1=reseller, slot 2=agen, dst).
 * Null jika tidak ada.
 *
 * Slot DI LUAR plafon (`isDiskonSlot` false) memulangkan null — artinya pembeli
 * jatuh ke diskon grup rata (§3 di kepala berkas), bukan ke harga penuh dan
 * bukan ke Rp 0. Itu arah gagal yang benar, dan sengaja dipertahankan sebagai
 * jaring: kalau suatu hari DB melewatkan slot di luar plafon, harga masih bisa
 * dijelaskan.
 */
function resolveVariantOverride(
  variant: ProductVariation,
  group: CustomerGroup
): VariantOverride | null {
  if (!group || !isDiskonSlot(group.slot)) return null;

  const value: number | undefined = variant[DISKON_SLOT_KEY[group.slot]];

  if (value === undefined || value <= 0) return null;
  const mode = variant.diskonMode ?? "persen";
  // Angka yang MUSTAHIL untuk modenya ditolak, bukan dipakai. Empat kolom
  // diskon varian tak punya satu pun CHECK di DB dan SATU kolom melayani DUA
  // mode, jadi `diskon_reseller = 25000` pada `diskon_mode = 'persen'` — salah
  // ketik yang wajar: nominal rupiah masuk ke kolom yang sedang bermode persen —
  // lolos sampai ke rumus, menghasilkan `base × (1 − 250)` yang negatif, lalu
  // diklem `Math.max(0, …)` menjadi **Rp 0**. Barang jadi bisa dibeli gratis;
  // PROD 2026-09-07 memuat 606 varian dalam keadaan itu dan 15 `order_items`
  // ber-`unit_price = 0` sudah lahir darinya.
  //
  // Ditolak, BUKAN ditafsirkan ulang jadi nominal: menebak maksud admin di
  // kolom uang persis kelas bug #2095. Yang tersisa adalah jatuh ke diskon grup
  // (§3 di kepala berkas) — pembeli tetap dapat potongan levelnya, tokonya tidak
  // menyerahkan barang gratis, dan angkanya bisa dijelaskan.
  if (!isUsableOverrideValue(mode, value)) return null;
  return { mode, value };
}

/**
 * Nilai override yang bisa dipakai APA ADANYA untuk modenya — bagian yang tidak
 * memerlukan harga dasar.
 *
 * Persen: 0 < value ≤ 100. Tepat 100 sah (gratis, dan bisa dinyatakan).
 * Nominal: batas atasnya bergantung `base`, jadi diperiksa di
 * `applyVariantOverride` yang memegang angka itu.
 */
function isUsableOverrideValue(mode: "persen" | "nominal", value: number): boolean {
  if (!Number.isFinite(value)) return false;
  if (mode === "persen") return value <= 100;
  return true;
}

/**
 * `null` = override tak bisa dipakai untuk harga dasar ini (potongan nominal
 * melebihi harganya → harga negatif). Sama seperti persen di luar rentang, ini
 * salah-isi data, bukan "gratis": potongan yang PERSIS sebesar harga (`base`)
 * tetap sah dan menghasilkan Rp 0.
 */
function applyVariantOverride(
  base: number,
  override: VariantOverride
): number | null {
  if (override.mode === "nominal") {
    if (override.value > base) return null;
    return Math.max(0, Math.round(base - override.value));
  }
  return Math.max(0, Math.round(base * (1 - override.value / 100)));
}

/**
 * Apakah override diskon varian ini TIDAK BISA dipakai — nilainya mustahil
 * untuk modenya, sehingga `getVariantPrice` mengabaikannya dan pembeli membayar
 * harga diskon grup biasa.
 *
 * Diekspor supaya layar yang MENGISI angkanya (form produk) memakai penilai yang
 * sama dengan yang MENGHITUNG harganya; kalau tidak, form akan menerima angka
 * yang diam-diam tak pernah berlaku.
 */
export function variantOverrideOutOfRange(
  mode: "persen" | "nominal" | undefined,
  value: number | undefined,
  base?: number
): boolean {
  if (value === undefined || value === null || value <= 0) return false;
  const m = mode ?? "persen";
  if (!isUsableOverrideValue(m, value)) return true;
  return m === "nominal" && base !== undefined && value > base;
}

/**
 * Batas bawah potongan NOMINAL yang masuk akal sebagai rupiah per pcs. Di bawah
 * ini hampir pasti angka PERSEN yang terketik di mode Nominal: harga jual grosir
 * fashion tidak pernah serendah itu, jadi "Rp 10" atau "Rp 15" bukan diskon.
 */
export const NOMINAL_DISCOUNT_SUSPECT_BELOW = 1000;

/**
 * Potongan ini TERPAKAI sebagai rupiah tapi angkanya sekecil persen — tanda
 * mode Nominal terpilih padahal maksudnya Persen.
 *
 * PROD 2026-11-03 (toko lealova, "legging loss"): 7 varian bermode nominal
 * dengan reseller Rp 10 / agen Rp 15 pada harga Rp 72.000. Mesin harga
 * menghitungnya dengan benar — potongan Rp 10 — sehingga keranjang tampil
 * "diskon Rp 10" dan tak satu pun layar form produk memberi isyarat. Ini
 * peringatan, BUKAN penolakan: `variantOverrideOutOfRange` tetap satu-satunya
 * penentu angka yang mustahil, dan harga yang dihasilkan tidak berubah.
 *
 * Hanya mode nominal dengan 0 < nilai < batas. Nol = tanpa diskon; persen
 * tidak pernah dicurigai di sini (persen kecil itu wajar).
 */
export function nominalDiscountLooksLikePercent(
  mode: "persen" | "nominal" | undefined,
  value: number | undefined | null
): boolean {
  if (mode !== "nominal") return false;
  if (value === undefined || value === null || !Number.isFinite(value)) return false;
  return value > 0 && value < NOMINAL_DISCOUNT_SUSPECT_BELOW;
}

/**
 * Kampanye promo yang MENANG untuk satu varian — SATU-SATUNYA penjawab
 * pertanyaan "harga varian ini berasal dari kampanye yang mana".
 *
 * Diekstrak (bukan disalin) justru karena ada DUA pemakai yang wajib sepakat:
 * `getVariantPrice` yang menghitung ANGKA-nya, dan resolver checkout yang
 * mencatat JEJAK-nya ke `order_items.promo_campaign_id` (mig 3650). Bila
 * keduanya punya aturan sendiri, suatu hari sebuah baris akan ditagih dengan
 * harga kampanye A sambil berlabel kampanye B — dan tak satu pun error muncul.
 *
 * `null` juga untuk varian "harga menyusul": tanpa harga dasar tak ada yang
 * bisa didiskon, jadi tak ada kampanye yang boleh diklaim.
 */
export function resolveVariantPromo(
  variant: ProductVariation,
  promos?: readonly ProductPromo[] | null,
  group?: CustomerGroup
): ProductPromo | null {
  if (variant.price_normal === undefined) return null;
  if (!promos || !promos.length) return null;
  // Kelayakan level (mig 3680) disaring DULU: kampanye yang tak berhak bagi
  // pembeli ini tak boleh ikut bersaing, dan tak boleh diklaim sebagai asal
  // harga. `group` opsional & undefined = tanpa level, jadi pemanggil lama
  // otomatis fail-closed terhadap kampanye ber-level.
  const eligible = promosForLevel(promos, group?.slot ?? null);
  if (!eligible.length) return null;
  // Ukuran diturunkan DI SINI dari nama varian (mig 3670). Sengaja bukan field
  // yang harus diisi mapper: satu mapper yang lupa akan membuat aturan ukuran
  // diam-diam tak berlaku di layar itu saja — lihat catatan `variantSizeLabel`.
  //
  // "Termurah" dinilai pada harga AKHIR pembeli ini: kampanye `stack` masih
  // dipotong diskon level sesudah harga promonya, jadi 90.000 (stack 10 %)
  // bisa lebih murah dari 80.000 (replace 20 %) bagi reseller 30 % → 63.000.
  const base = variant.price_normal;
  const size = variantSizeLabel(variant.name);
  const levelPrice = () => applyLevelLadder(variant, group ?? null, base).price;
  const best = pickCheapestPromo(eligible, base, size, (promoPrice, promo) => {
    if (promoLevelPolicy(promo) === "stack") {
      return applyLevelLadder(variant, group ?? null, promoPrice).price;
    }
    // never_worse (mig 6010): kampanye replace yang tak boleh lebih mahal dari
    // harga level — bersaing pada angka yang benar-benar dibayar.
    return promo.neverWorse ? Math.min(promoPrice, levelPrice()) : promoPrice;
  });
  if (!best) return null;
  // never_worse: bila harga LEVEL lebih murah dari harga promo pemenang,
  // kampanye TIDAK diklaim — harga, badge, dan jejak jatuh ke tangga level
  // biasa (usulan 1.4: promo tak pernah membuat reseller bayar lebih mahal).
  // Seri → promo yang menang (kampanyenya tercatat di jejak).
  if (promoLevelPolicy(best) === "replace" && best.neverWorse) {
    if (levelPrice() < applyPromo(base, best, size)) return null;
  }
  return best;
}

/**
 * Tangga DISKON LEVEL (§2–§3 di kepala berkas) atas harga dasar `base`:
 * override per-varian untuk slot grup pembeli menang; bila tak ada / tak bisa
 * dipakai (nominal > base), diskon grup rata. SATU definisi untuk tiga
 * pemakai yang wajib sepakat: cabang tanpa promo, cabang promo `stack`
 * (basis = harga coret), dan penilai "termurah" di `resolveVariantPromo`.
 */
function applyLevelLadder(
  variant: ProductVariation,
  group: CustomerGroup,
  base: number
): { price: number; cut: PriceCut | null } {
  // §1a: aturan merek level (sudah diselesaikan per produk) menggantikan
  // override per-varian dan diskon rata — tak satu pun di bawah dinilai.
  const brandPersen = levelBrandPersen(group);
  if (brandPersen !== null) {
    return {
      price: Math.max(0, Math.round(base * (1 - brandPersen / 100))),
      cut: { source: "group_brand", mode: "persen", value: brandPersen }
    };
  }
  const override = resolveVariantOverride(variant, group);
  if (override) {
    const price = applyVariantOverride(base, override);
    // `null` = override tak terpakai (lihat applyVariantOverride) → JATUH ke
    // diskon grup, bukan ke Rp 0.
    if (price !== null) {
      return { price, cut: { source: "override", mode: override.mode, value: override.value } };
    }
  }
  const g = groupTier(group);
  return {
    price: applyGroupDiscount(base, group) ?? base,
    cut: g ? { source: "group", mode: g.mode, value: g.value } : null
  };
}

/**
 * Satu potongan (persen dari harga / nominal rupiah per pcs) atas harga dasar —
 * pembulatan `Math.round` dan klem ≥ 0 yang SAMA dengan `applyVariantOverride`.
 * Dipakai jejak potongan baris keranjang (`cartItemPrice.ts`) dan Ubah Diskon
 * keranjang admin (`cartManualPrice.ts`, mig 7060): kolom uang tak boleh punya
 * dua aturan pembulatan.
 */
export function applyPriceCut(
  base: number,
  cut: { mode: "persen" | "nominal"; value: number }
): number {
  return cut.mode === "nominal"
    ? Math.max(0, Math.round(base - cut.value))
    : Math.max(0, Math.round(base * (1 - cut.value / 100)));
}

/**
 * Harga varian untuk grup tertentu.
 * PROMO menang mutlak; bila tidak ada promo, override diskon per-varian menang;
 * bila itu pun tidak ada, pakai diskon grup.
 * Fallback ke price_normal produk jika varian tidak punya price_normal.
 *
 * `promos` = kampanye yang SUDAH cocok dengan produk ini (promosForProduct).
 * Sengaja menerima DAFTAR, bukan satu promo: pemenang "termurah" hanya bisa
 * ditentukan setelah harga dasar varian diketahui — dan fungsi inilah satu-
 * satunya tempat yang memegang angka itu.
 */
export function getVariantPrice(
  variant: ProductVariation,
  group: CustomerGroup,
  promos?: readonly ProductPromo[] | null
): number | undefined {
  // Didelegasikan supaya ANGKA dan PENJELASAN tier (dipakai voucher) lahir dari
  // satu badan yang sama — lihat `PriceTier`.
  return explainVariantPrice(variant, group, promos).price;
}

/**
 * `getVariantPrice` berikut TIER yang membentuknya (lihat `PriceTier`).
 * Badan aslinya `getVariantPrice`; fungsi itu kini hanya membaca `.price`.
 */
export function explainVariantPrice(
  variant: ProductVariation,
  group: CustomerGroup,
  promos?: readonly ProductPromo[] | null
): VariantPriceExplanation {
  // mig 7900: tambahan di LUAR seluruh potongan — dihitung sekali, ditempel di
  // ujung tiap cabang. Tanpa harga dasar tak ada yang bisa ditempeli.
  const extra = variantPriceExtra(variant);
  if (variant.price_normal === undefined) {
    // Harga menyusul → tak ada angka untuk didiskon, promo pun tak berlaku.
    return {
      price: applyGroupDiscount(variant.price_normal, group),
      tier: null,
      tier2: null,
      cuts: [],
      strikePrice: undefined,
      promo: null,
      priceExtra: extra
    };
  }

  const promo = resolveVariantPromo(variant, promos, group);
  if (promo) {
    // Ukuran dihitung ulang dari sumber yang SAMA dengan resolveVariantPromo —
    // memakai ukuran berbeda di sini berarti harga bisa datang dari aturan yang
    // bukan milik kampanye yang barusan terpilih.
    const size = variantSizeLabel(variant.name);
    const rule = promoDiscountForSize(promo, size);
    const coret = applyPromo(variant.price_normal, promo, size);
    // Aturan yang tak bisa dipakai tak pernah sampai sini (`promoRuleUsable`
    // di pickCheapestPromo); pagar ini menjaga bila suatu hari ia dilonggarkan.
    const promoCut: PriceCut | null =
      rule && Number.isFinite(rule.value) && rule.value > 0
        ? { source: "promo", mode: rule.mode, value: rule.value }
        : null;

    if (promoLevelPolicy(promo) === "stack") {
      // DISKON HARGA CORET: tangga level dihitung dari harga coret, bukan dari
      // price_normal. Yang dicoret di layar = harga coret bila level memotong
      // lagi; kalau pembeli tak punya potongan level, coretannya harga umum.
      const level = applyLevelLadder(variant, group, coret);
      const cuts: PriceCut[] = [];
      if (promoCut) cuts.push(promoCut);
      if (level.cut) cuts.push(level.cut);
      return {
        price: level.price + extra,
        tier: tierOf(cuts[0]),
        tier2: tierOf(cuts[1]),
        cuts,
        strikePrice: (promoCut && level.cut ? coret : variant.price_normal) + extra,
        promo,
        priceExtra: extra
      };
    }

    return {
      price: coret + extra,
      tier: tierOf(promoCut),
      tier2: null,
      cuts: promoCut ? [promoCut] : [],
      strikePrice: variant.price_normal + extra,
      promo,
      priceExtra: extra
    };
  }

  const level = applyLevelLadder(variant, group, variant.price_normal);
  return {
    price: level.price + extra,
    tier: tierOf(level.cut),
    tier2: null,
    cuts: level.cut ? [level.cut] : [],
    strikePrice: variant.price_normal + extra,
    promo: null,
    priceExtra: extra
  };
}

/** Diskon grup sebagai tier; grup tanpa diskon = tanpa tier. */
function groupTier(group: CustomerGroup): PriceTier {
  if (!group || !Number.isFinite(group.discountPersen) || group.discountPersen <= 0) return null;
  return { mode: "persen", value: group.discountPersen };
}

/**
 * HARGA YANG BENAR-BENAR DILIHAT PEMBELI untuk sebuah pilihan varian — berikut
 * kampanye yang membentuknya. SATU definisi untuk dua pemakai yang WAJIB
 * sepakat: tampilan (kartu & halaman produk) dan resolver checkout yang
 * mengunci `order_items.unit_price`.
 *
 * ── KENAPA ADA ────────────────────────────────────────────────────────────
 * Model harga di kepala berkas ini menyatakan "harga dasar = price_normal
 * varian (FALLBACK price_normal produk)", tapi `getVariantPrice` tak pernah
 * mengimplementasikan fallback itu — hanya `getProductPrice`. Akibatnya dua
 * lapis menjawab beda untuk varian yang tak punya harga sendiri:
 *   • halaman produk  → `getVariantPrice(...) ?? getProductPrice(...)` → ADA harga
 *   • resolver checkout → `getVariantPrice(...)` saja → `undefined` → unit_price NULL
 * Pembeli melihat harga, notanya lahir **Rp 0** ("harga menyusul") dan admin
 * harus mengetik ulang nominalnya. Terjadi nyata pada produk yang variannya
 * baru ditambahkan tanpa harga sementara varian lain sudah berharga (PROD
 * 2026-08-14: "Lili Hijab New", 15 varian berharga + 6 tanpa harga).
 *
 * Fallback SENGAJA memakai varian PERWAKILAN (varian pertama yang berharga) —
 * bukan menyuntikkan harga produk ke varian terpilih — supaya hasilnya identik
 * dengan yang dipajang kartu & halaman produk, termasuk override diskon per
 * varian milik perwakilan itu.
 *
 * `price` `undefined` = benar-benar "harga menyusul" (tak ada satu pun sumber
 * harga) — alur preorder tanpa harga tetap utuh.
 *
 * ── BATAS FALLBACK: `unpricedVariant` (natasyashop #2095, 2026-09-05;
 *    dilonggarkan atas keputusan pemilik 2026-09-07) ─────────────────────────
 * Fallback perwakilan di atas menutup "nota lahir Rp 0", tetapi ia MEMBAYAR-nya
 * dengan harga varian LAIN. Pada produk besar itu bukan selisih receh: PROD
 * "Dwijana Family Series" (900 kombinasi, 153 berharga) memberi perwakilan
 * `sort_order = 0` = "Rasa / Dress Style 1 (inner+outer) / XS" Rp 695.000,
 * sehingga pembeli yang memilih **Blouse** (seharusnya Rp 445.000) ditagih
 * 695.000 × 0,75 = Rp 521.250 — dan TANPA satu pun tanda di layar, karena
 * `products.price_normal` (MIN seluruh varian, Rp 100.000) membuat tiga layar
 * menyimpulkan "tidak sedang diskon": halaman produk & keranjang tak mencoret
 * apa pun, dan nota mencetak "–" di kolom Harga Jual Umum + Diskon
 * (`invoicePdf.ts` `paidAboveNormal`).
 *
 * Jadi fallback dibatasi ke maksudnya semula. `unpricedVariant` menandai kasus
 * yang BUKAN "harga menyusul biasa" melainkan SETELAN HARGA BELUM LENGKAP:
 * angka perwakilan yang ada di `price` TIDAK BOLEH masuk kolom uang.
 *
 * ⚠️ YANG BERUBAH 2026-09-07 — PEMESANANNYA TIDAK LAGI DIBLOKIR. Perbaikan
 * 2026-09-05 (opsi B) menutup lubang uangnya dengan MENOLAK pesanan: storefront
 * mematikan tombolnya, resolver checkout melempar. Pemilik menyatakan efek itu
 * tidak dikehendaki — produk yang harganya belum disetel HARUS tetap bisa
 * dipesan. Gerbangnya karena itu digeser dari "tolak pesanan" menjadi "tolak
 * ANGKANYA": varian seperti ini dipesan sebagai **harga menyusul**
 * (`unit_price` NULL), persis alur PO tanpa harga yang sudah ada, dan admin
 * mengisi nominalnya saat membuat nota. Yang tetap haram adalah menagih harga
 * varian perwakilan — itu bug uang yang melahirkan #2095.
 *
 * Jalur uang WAJIB melewatkan `SelectionPrice` lewat `sellableSelectionPrice()`
 * di bawah, bukan membaca `price` langsung.
 *
 * PEMBEDANYA KESERAGAMAN HARGA, bukan sekadar "ada varian lain yang berharga".
 * Fallback hanya menebak kalau ada yang bisa ditebak salah:
 *   · harga varian SERAGAM (semua varian berharga bernilai sama) → perwakilan
 *     PASTI benar. Ini alur materialisasi malas yang sah: varian yang lahir
 *     on-demand (`resolve_or_create_preorder_variant`, mig 3640/4640) SENGAJA
 *     tidak mewarisi harga, jadi menebak harganya justru yang benar untuk
 *     produk PO besar yang harganya satu untuk semua (bug live ZURIAH).
 *   · harga varian BERBEDA-BEDA → perwakilan cuma varian yang kebetulan
 *     `sort_order`-nya paling kecil. Ditandai: pesanan lewat, ANGKANYA tidak.
 *   · produk yang BELUM punya harga varian sama sekali → `price_normal` produk
 *     memang berlaku untuk semua varian; alur preorder "harga menyusul" utuh.
 *
 * `price` pada kasus itu tetap diisi harga perwakilan supaya layar admin yang
 * hanya MEMBACA (kartu katalog, pratinjau) tak berubah jadi kosong; yang
 * berubah adalah siapa yang boleh MENAGIHKAN-nya.
 */
export type SelectionPrice = {
  price: number | undefined;
  /**
   * Harga normal SUMBER YANG SAMA dengan `price` — varian terpilih, atau
   * perwakilan bila `price` datang dari fallback. Inilah satu-satunya angka
   * yang sah dicoret di samping `price`.
   *
   * JANGAN diganti `products.price_normal`: ProductForm menyimpannya sebagai
   * MIN harga efektif SELURUH varian, jadi pada produk ber-harga campuran ia
   * LEBIH MURAH dari harga yang sedang dipajang — pembandingnya lalu menyimpulkan
   * "pembeli bayar di ATAS harga normal" dan potongan yang nyata hilang dari
   * layar (larangan yang sama sudah tertulis di `getProductNormalPrice` dan
   * `cartItemPrice.ts`).
   */
  normalPrice: number | undefined;
  /** Kampanye yang membentuk `price` — jejak untuk `order_items` (mig 3650). */
  promo: ProductPromo | null;
  /**
   * Potongan yang membentuk `price` dari `normalPrice` (promo / override /
   * grup) — bahan voucher untuk menerapkan ulang tier di atas harga umum yang
   * sudah dipotong voucher. Selalu sejalan dengan `price`; `null` = tanpa
   * potongan ATAU harga menyusul.
   */
  tier: PriceTier;
  /** Potongan KEDUA (promo coret + level) — tier2 mesin voucher; `null` selain itu. */
  tier2: PriceTier;
  /** Seluruh potongan urut penerapan — bahan badge & jejak `order_items.*_discount_*`. */
  cuts: readonly PriceCut[];
  /**
   * Angka yang DICORET di layar (harga sebelum potongan terakhir). Sumber yang
   * SAMA dengan `price`, jadi ia boleh dicoret di sampingnya; berbeda dari
   * `normalPrice` hanya pada reseller di promo coret (90.000 vs 100.000).
   */
  strikePrice: number | undefined;
  /**
   * Harga tambahan yang TERMASUK di `price`/`strikePrice` (mig 7900), sumber
   * yang sama (varian terpilih / warisan / perwakilan). `normalPrice` tetap
   * DASAR: kolom uang (`normal_price` voucher, `manual_price_normal`,
   * `unit_normal_price` nota) tak pernah memuat tambahan; potret tambahannya
   * ditulis terpisah ke `order_items.unit_price_extra`.
   */
  priceExtra: number;
  /** Harga datang dari perwakilan/produk, bukan dari varian yang dipilih. */
  fromFallback: boolean;
  /**
   * Varian yang DIPILIH tak punya harga sendiri sementara produknya punya
   * varian berharga dengan nilai BERBEDA-BEDA = setelan harga belum lengkap.
   * `price` di sini milik varian PERWAKILAN, jadi ia tak boleh ditagihkan —
   * tapi pesanannya tetap boleh jalan sebagai harga menyusul. Jalur uang
   * memakai `sellableSelectionPrice()`, bukan `price` — lihat catatan di atas.
   */
  unpricedVariant: boolean;
};

/**
 * Angka yang BOLEH masuk kolom uang dari sebuah `SelectionPrice`, berikut
 * kampanye yang boleh diklaim menyertainya.
 *
 * SATU definisi untuk semua jalur uang (keranjang storefront, pesan WhatsApp,
 * dan resolver checkout yang mengunci `order_items.unit_price`) supaya tak ada
 * satu pun dari mereka yang diam-diam memakai `selection.price` mentah: pada
 * `unpricedVariant` angka itu milik varian LAIN, dan menagihkannya persis bug
 * natasyashop #2095.
 *
 * `undefined` = harga menyusul. Pesanannya SAH dan tetap dibuat — `unit_price`
 * NULL lalu diisi admin saat membuat nota (keputusan pemilik 2026-09-07).
 * Promo ikut dinolkan: tanpa harga tak ada kampanye yang membentuk apa pun,
 * jadi tak ada yang boleh dicatat ke `order_items.promo_campaign_id`.
 */
export function sellableSelectionPrice(selection: SelectionPrice): {
  price: number | undefined;
  promo: ProductPromo | null;
  tier: PriceTier;
  tier2: PriceTier;
  cuts: readonly PriceCut[];
  normalPrice: number | undefined;
  /** Tambahan yang TERMASUK di `price` — potret `order_items.unit_price_extra`. */
  priceExtra: number;
} {
  if (selection.unpricedVariant) {
    return {
      price: undefined, promo: null, tier: null, tier2: null, cuts: [],
      normalPrice: undefined, priceExtra: 0
    };
  }
  const priced = selection.price !== undefined;
  return {
    price: selection.price,
    promo: selection.promo,
    tier: priced ? selection.tier : null,
    tier2: priced ? selection.tier2 : null,
    // Jejak potongan ke `order_items` (promo_/level_discount_*): hanya bila ada
    // harga yang benar-benar ditagihkan.
    cuts: priced ? selection.cuts : [],
    // Harga umum yang sah dicoret di samping `price` — sumber yang SAMA.
    normalPrice: priced ? selection.normalPrice : undefined,
    priceExtra: priced ? selection.priceExtra : 0
  };
}

/**
 * Rentang harga varian: dari daftar varian bila ada yang berharga, kalau tidak
 * dari ringkasan `priceRange` (mode malas). `null` = tak ada harga varian yang
 * diketahui — bukan "seragam", melainkan "tak ada yang bisa ditebak salah".
 *
 * MODE MALAS — sejak Fase 0 (mig 5450) ringkasan ini datang dari AGREGAT SQL
 * (`product_variant_price_range`), bukan dari cuplikan baris yang sempat
 * terambil. Cuplikan lama bisa melaporkan "seragam" untuk produk yang
 * sebenarnya campuran: diukur di PROD, sampel 201 baris `RONA NIRMALA` memuat
 * NOL baris berharga padahal produknya punya 10 (Rp 350.000–1.045.000),
 * sehingga halaman memajang `products.price_normal` Rp 350.000 sebagai harga
 * pasti untuk 2.470 kombinasi.
 *
 * Penentu yang MENGIKAT tetap di resolver checkout
 * (`buildCheckoutLineResolver`), yang selalu memegang daftar varian PENUH;
 * ringkasan ini membuat layar berhenti menjanjikan angka yang tak akan dikunci
 * checkout.
 */
function variantPriceSpread(catalog: {
  variations: readonly ProductVariation[];
  priceRange?: VariantPriceSummary | null;
}): { min: number; max: number } | null {
  let min: number | undefined;
  let max: number | undefined;
  for (const v of catalog.variations) {
    // mig 7900: keseragaman dinilai atas DASAR + TAMBAHAN — dasar seragam dengan
    // tambahan berbeda tetap "campuran": perwakilan tak boleh menebak tambahan
    // varian lain (kelas #2095). `priceRange` dari server pun rentang TOTAL.
    const price = v.price_normal === undefined ? undefined : v.price_normal + variantPriceExtra(v);
    if (price === undefined) continue;
    if (min === undefined || price < min) min = price;
    if (max === undefined || price > max) max = price;
  }
  if (min !== undefined && max !== undefined) return { min, max };
  return catalog.priceRange ?? null;
}

/**
 * Ringkasan harga varian BERHARGA tanpa barisnya: rentang TOTAL (dasar +
 * tambahan — RPC `min_total`/`max_total`, atau `variantOwnTotal` atas baris),
 * plus bahan uji kesepakatan tambahan (bughunt D14-2) bila diketahui:
 *   • `extraMin`/`extraMax` — rentang TAMBAHAN varian berharga, dari pemanggil
 *     yang memegang barisnya (Detail Produksi, ekspor);
 *   • `minBase` — MIN DASAR (`min_price`) dari RPC yang memulangkan total.
 *     Total seragam yang ≠ dasar terendah = ada tambahan yang pembagiannya tak
 *     diketahui.
 *   • `maxBase` — MAX DASAR (`max_price`), bersama `minBase` (bughunt D15-20):
 *     dasar seragam + total seragam ⇒ tambahan seragam = total − dasar, jadi
 *     varian tanpa harga sendiri yang tambahannya SAMA boleh meminjam — cermin
 *     checkout yang memegang daftar varian penuh.
 */
export type VariantPriceSummary = {
  min: number;
  max: number;
  minBase?: number;
  maxBase?: number;
  extraMin?: number;
  extraMax?: number;
};

/**
 * Bughunt D14-2 — bolehkah varian TANPA harga dasar sendiri, bertambahan `own`,
 * meminjam TOTAL seragam ringkasan varian berharga `summary` (null = tak ada
 * varian berharga → lapis `products.price_normal`, yang tak pernah bertambahan)?
 * Total itu memuat tambahan PEMBERINYA, jadi sah hanya bila tambahan setiap
 * varian berharga = `own`; tak diketahui = fail-closed kecuali `own` 0 (aturan
 * pra-7900 utuh). Dipakai cermin admin (`variantSellingPrice`: badge, HPP,
 * label, iPOS, invoice supplier) dan lapis akhir toko (`own` 0 = "ringkasan
 * bebas tambahan?", `borrowedExtraMismatch`).
 */
export function borrowedExtraAgrees(
  own: number,
  summary: VariantPriceSummary | null | undefined
): boolean {
  if (!summary) return own === 0;
  if (summary.extraMin !== undefined && summary.extraMax !== undefined) {
    return summary.extraMin === own && summary.extraMax === own;
  }
  if (summary.minBase !== undefined && summary.maxBase !== undefined) {
    // D15-20: dasar seragam & total seragam ⇒ tambahan seragam (total − dasar).
    return (
      summary.min === summary.max &&
      summary.minBase === summary.maxBase &&
      summary.min - summary.minBase === own
    );
  }
  if (summary.minBase !== undefined) {
    return own === 0 && summary.min === summary.max && summary.minBase === summary.min;
  }
  return own === 0;
}

/**
 * Bughunt D14-2 — varian terpilih TANPA harga dasar sendiri meminjam harga
 * (perwakilan / lapis akhir) BESERTA tambahan pemberinya. Keseragaman di
 * `variantPriceSpread` menilai total varian BERHARGA saja, jadi tambahan milik
 * varian terpilih tak pernah diuji: XXL (dasar kosong, +20.000) meminjam
 * 100.000 + 0 milik S (tambahannya hilang), dan S tanpa harga meminjam
 * 100.000 + 20.000 milik XXL (kelas #2095). Pinjaman hanya sah bila tambahannya
 * SEPAKAT — cermin SQL `voucher_evaluate_cart` (dasar DAN tambahan masing-masing
 * seragam). `true` = tak sepakat → harga menyusul.
 *   • ada varian berharga → tambahan SETIAP varian berharga = `own` (total
 *     seragam + tambahan seragam ⇒ dasar seragam);
 *   • tanpa varian berharga (mode malas / belum berharga) → lapis akhir
 *     bertambahan 0: `own` wajib 0 dan ringkasan server bebas tambahan.
 * Semua tambahan 0 (fixture emas) → selalu `false`: perilaku pra-7900 utuh.
 */
function borrowedExtraMismatch(
  own: number,
  catalog: {
    variations: readonly ProductVariation[];
    priceRange?: VariantPriceSummary | null;
    priceNormal?: number;
  }
): boolean {
  let pricedSeen = false;
  for (const v of catalog.variations) {
    if (v.price_normal === undefined) continue;
    pricedSeen = true;
    if (variantPriceExtra(v) !== own) return true;
  }
  if (pricedSeen) return false;
  // Tanpa varian berharga di daftar: pemberinya lapis `products.price_normal`.
  // Tambahan 0 → ringkasan server wajib bebas tambahan (total seragam ≠ dasar
  // terendah = pembagiannya tak diketahui). Tambahan ≠ 0 (mode malas, D15-20)
  // → hanya bila ringkasan memastikan SEMUA varian berharga ber-(dasar,
  // tambahan) seragam dengan tambahan = `own`, dan dasar itu = lapis akhir —
  // checkout (daftar penuh) meminjam pasangan yang sama.
  if (own === 0) return !borrowedExtraAgrees(0, catalog.priceRange);
  const range = catalog.priceRange;
  return (
    !range ||
    range.maxBase === undefined ||
    range.minBase !== catalog.priceNormal ||
    !borrowedExtraAgrees(own, range)
  );
}

export function resolveSelectionPrice(
  selected: ProductVariation | undefined,
  catalog: {
    variations: readonly ProductVariation[];
    /** `products.price_normal` — lapis terakhir. */
    priceNormal?: number;
    /**
     * Rentang harga varian produk ini — sejak mig 7900 rentang TOTAL (dasar +
     * tambahan; RPC `min_total`/`max_total`), untuk pemanggil yang TIDAK
     * memegang daftar variannya — halaman produk mode malas membuangnya
     * (`variantsLazy`, `variations: []`), sehingga tanpa ringkasan ini ia
     * mustahil tahu harga produknya seragam atau tidak, dan akan memajang
     * `products.price_normal` (MIN seluruh varian) sebagai harga varian yang
     * dipilih. Diabaikan bila `variations` sudah memuat varian berharga.
     *
     * Sejak Fase 0 (mig 5450) angkanya AGREGAT, bukan cuplikan — lihat catatan
     * di `variantPriceSpread`.
     */
    priceRange?: VariantPriceSummary | null;
    /**
     * Rentang harga GAGAL diketahui (agregat tak terjawab: jaringan, timeout,
     * grant). BUKAN sama dengan `priceRange: null`.
     *
     * `null` berarti "produk ini memang belum punya harga varian" — keadaan sah
     * yang membuat `products.price_normal` berlaku untuk semua kombinasi.
     * `priceUncertain` berarti "kami tidak tahu", dan menyamakan keduanya
     * membuat kegagalan sesaat berubah jadi izin menagihkan angka perwakilan —
     * kelas bug #2095. Karena itu ia FAIL-CLOSED: diperlakukan seperti harga
     * CAMPURAN, sehingga varian yang tak punya harga sendiri jatuh ke "harga
     * menyusul" alih-alih meminjam harga varian lain.
     */
    priceUncertain?: boolean;
    /**
     * SUMBU HARGA (2026-10-20, mig 7680): produk menyatakan grup penentu harga
     * (`products.price_affecting_groups` tak kosong) — dibaca lewat RPC
     * `product_has_price_axis` karena kolomnya sengaja tak terbaca anon.
     *
     * `true` → keseragaman SELURUH varian berharga BUKAN bukti (hanya satu
     * nilai sumbu yang berharga pun sudah "seragam" — Mukena 17 Sep: RAJWA
     * 170.000 × 7, NAJWA dikunci 136.000). Baris yang ADA tapi tanpa harga
     * hanya boleh berharga dari `inherited` (saudara pada sumbu yang sama,
     * RPC `preorder_inherited_pricing`); tanpanya ia harga menyusul.
     * `false` → aturan lama (putusan 2026-09-07 untuk produk tanpa sumbu).
     * `null` → tak diketahui (RPC gagal / produk toko lain) → fail-closed
     * seperti `true`. Absen = pemanggil tak memuatnya → aturan lama.
     */
    priceAxis?: boolean | null;
    /**
     * FASE 4 (mig 5510) — harga yang AKAN diwarisi kombinasi terpilih begitu
     * barisnya dibuat, dihitung server lewat `preorder_inherited_pricing`.
     * HANYA untuk kombinasi yang barisnya BELUM ada — gerbangnya
     * `shouldUseInheritedPricing` (`lazyVariants.ts`).
     *
     * Kenapa ini ada: materialisasi sengaja hanya terjadi saat menambah ke
     * keranjang, jadi di mode malas kombinasi yang belum pernah dipesan tak
     * punya baris — dan tanpa ringkasan ini layar jatuh ke varian PERWAKILAN
     * lalu menahan angkanya ("harga menyusul") padahal harganya SUDAH pasti.
     *
     * ANGKANYA DATANG DARI SERVER, TITIK. Jangan pernah menghitung harga slot
     * di TypeScript: RPC itu fungsi yang PERSIS SAMA dengan yang menulis
     * `price_normal` baris barunya, jadi layar dan nota mustahil melenceng.
     * Rumus tandingan di sini akan mengembalikan kelas bug #2095.
     *
     * `priceNormal: null` = gerbang slot tidak terpenuhi = tetap harga
     * menyusul. `undefined`/tak diisi = pemanggil tak memakai jalur ini
     * (perilaku persis seperti sebelum Fase 4).
     */
    inherited?: {
      priceNormal: number | null;
      diskonMode?: "persen" | "nominal";
      diskonReseller?: number;
      diskonAgen?: number;
      diskonSlot3?: number;
      diskonSlot4?: number;
      // Slot 5–8 (mig 5600): RPC & `resolvePublicInheritedPricing` sudah
      // memulangkannya; audit A1 (2026-09-16) menemukan tipe ini tertinggal di
      // 4 slot, sehingga pembeli grup slot 5–8 melihat harga GRUP di halaman
      // produk mode malas padahal checkout menagih override variannya.
      diskonSlot5?: number;
      diskonSlot6?: number;
      diskonSlot7?: number;
      diskonSlot8?: number;
      diskonSlot9?: number;
      diskonSlot10?: number;
      diskonSlot11?: number;
      diskonSlot12?: number;
      diskonSlot13?: number;
      diskonSlot14?: number;
      diskonSlot15?: number;
      /** Tambahan yang diwarisi (mig 7900) — RPC hanya memberinya bersama
       *  `priceNormal` (saudara sepakat pada keduanya); absen/null = 0. */
      priceExtra?: number | null;
    } | null;
    /** Nama kombinasi terpilih — dipakai aturan promo ber-ukuran saat
     *  barisnya belum ada (`deriveVariantLabel`). */
    inheritedName?: string;
  },
  group: CustomerGroup,
  promos?: readonly ProductPromo[] | null
): SelectionPrice {
  // Seragam? Dihitung SEBELUM cabang mana pun supaya ketiga cabang menjawab
  // pertanyaan "boleh menebak?" dengan aturan yang sama.
  const spread = variantPriceSpread(catalog);
  // SUMBU HARGA (2026-10-20): baris yang ADA tapi tanpa harga pada produk
  // ber-sumbu tak boleh ditebak dari keseragaman varian lain — hanya cabang
  // `inherited` di bawah (saudara pada sumbu yang sama) yang boleh memberi
  // angka; selain itu harga menyusul. Sumbu tak diketahui (`null`) = ragu =
  // fail-closed, idiom `priceUncertain`.
  const axisBound =
    selected !== undefined &&
    selected.price_normal === undefined &&
    (catalog.priceAxis === true || catalog.priceAxis === null);
  // Bughunt D14-2: pinjaman harga ikut meminjam TAMBAHAN pemberinya — sah hanya
  // bila tambahan varian terpilih sepakat (`borrowedExtraMismatch`).
  const extraBorrowed =
    selected !== undefined &&
    selected.price_normal === undefined &&
    borrowedExtraMismatch(variantPriceExtra(selected), catalog);
  const ambiguous =
    selected !== undefined &&
    (axisBound ||
      extraBorrowed ||
      catalog.priceUncertain === true ||
      (spread !== null && spread.min !== spread.max));
  if (selected && selected.price_normal !== undefined) {
    const own = explainVariantPrice(selected, group, promos);
    return {
      ...selectionFromExplanation(own, selected.price_normal),
      fromFallback: false,
      unpricedVariant: false
    };
  }

  // FASE 4: kombinasi ini belum punya baris, tapi server sudah memberi tahu
  // harga yang AKAN diwarisinya. Itu bukan tebakan dan bukan harga varian
  // lain — jadi ia diperlakukan persis seperti harga varian sendiri: BUKAN
  // fallback, BUKAN `unpricedVariant`. Baris yang SUDAH ada tapi belum
  // berharga tak boleh sampai ke sini (`shouldUseInheritedPricing`): warisan
  // tak pernah ditulis ke baris yang sudah ada, jadi checkout tak mengenalnya.
  if (catalog.inherited && catalog.inherited.priceNormal !== null) {
    const inheritedVariant: ProductVariation = {
      id: selected?.id ?? "",
      // Nama menentukan label ukuran untuk kampanye ber-aturan-ukuran; memakai
      // nama kosong akan diam-diam melewati aturan itu.
      name: selected?.name ?? catalog.inheritedName ?? "",
      sku: selected?.sku ?? "",
      optionIds: selected?.optionIds,
      price_normal: catalog.inherited.priceNormal,
      price_extra: catalog.inherited.priceExtra ?? 0,
      diskonMode: catalog.inherited.diskonMode
    };
    // SELURUH slot lewat peta tunggal (mig 5600) — bukan empat baris tulis
    // tangan yang tertinggal saat plafon naik (audit A1).
    for (const slot of DISKON_SLOTS) {
      const key = DISKON_SLOT_KEY[slot];
      const v = catalog.inherited[key];
      if (v !== undefined) inheritedVariant[key] = v;
    }
    const inherited = explainVariantPrice(inheritedVariant, group, promos);
    return {
      ...selectionFromExplanation(inherited, inheritedVariant.price_normal),
      fromFallback: false,
      unpricedVariant: false
    };
  }

  // Varian PERWAKILAN produk: yang PERTAMA punya price_normal. Dipilih di SATU
  // tempat ini saja supaya harga yang dipajang (`getProductPrice`) dan harga
  // yang dicoret (`getProductNormalPrice`) tak mungkin datang dari varian
  // berbeda — kalau berbeda, coretannya jadi salah.
  const representative = catalog.variations.find(
    (v) => v.price_normal !== undefined
  );
  if (representative) {
    const rep = explainVariantPrice(representative, group, promos);
    return {
      ...selectionFromExplanation(rep, representative.price_normal),
      fromFallback: true,
      unpricedVariant: ambiguous
    };
  }

  // Tanpa varian ber-harga: promo tetap berlaku atas price_normal produk, lalu
  // diskon level — tangga yang SAMA dengan varian sungguhan, lewat varian
  // sintetis tanpa nama & tanpa override. Nama kosong ⇒ label ukuran null ⇒
  // kampanye ber-aturan-ukuran EKSKLUSIF tak ikut bersaing (jatuh ke harga
  // level) — arah gagal yang benar untuk harga yang tak bisa dipastikan; dan
  // tanpa kolom `diskon_*` ⇒ override tak pernah menyala, jadi diskon grup.
  // Bila `priceNormal` pun tak ada, seluruh rantai memulangkan `undefined`.
  //
  // Tanpa varian berharga yang terlihat, `products.price_normal` berlaku untuk
  // SEMUA varian ("harga menyusul"/harga tunggal) — KECUALI bila ringkasan
  // `priceRange` memberi tahu bahwa harga varian produk ini sebenarnya
  // berbeda-beda. Itulah keadaan halaman produk mode malas: daftar variannya
  // sengaja dibuang, dan `products.price_normal` yang tersisa adalah MIN
  // seluruh varian — angka "mulai dari", bukan harga varian yang dipilih.
  // Lapis `products.price_normal` sendiri tak pernah bertambahan. Satu
  // pengecualian (bughunt D15-20): varian terpilih tanpa harga sendiri yang
  // tambahannya SEPAKAT dengan ringkasan server (`borrowedExtraMismatch`) —
  // lapis ini berdiri untuk varian berharga yang tak termuat (mode malas), jadi
  // ia membawa tambahan yang sama dengan pinjaman checkout. Tambahan 0 = lama.
  const tailExtra =
    selected !== undefined && selected.price_normal === undefined && !extraBorrowed
      ? variantPriceExtra(selected)
      : 0;
  const synthetic: ProductVariation = {
    id: "",
    name: "",
    sku: "",
    price_normal: catalog.priceNormal,
    price_extra: tailExtra
  };
  const tail = explainVariantPrice(synthetic, group, promos);
  return {
    ...selectionFromExplanation(tail, catalog.priceNormal),
    fromFallback: true,
    unpricedVariant: ambiguous
  };
}

/** Bagian `SelectionPrice` yang datang langsung dari penjelasan harga varian. */
function selectionFromExplanation(
  explained: VariantPriceExplanation,
  normalPrice: number | undefined
): Omit<SelectionPrice, "fromFallback" | "unpricedVariant"> {
  return {
    price: explained.price,
    normalPrice,
    promo: explained.promo,
    tier: explained.tier,
    tier2: explained.tier2,
    cuts: explained.cuts,
    strikePrice: explained.strikePrice,
    priceExtra: explained.priceExtra
  };
}

/**
 * Harga produk untuk KARTU berikut coretan & potongannya — satu panggilan
 * untuk `price`, `strikePrice`, `cuts`, `promo`, supaya angka yang dipajang,
 * angka yang dicoret, dan badge mustahil datang dari varian berbeda.
 */
export function getProductPriceView(
  product: Product,
  group: CustomerGroup,
  promos?: readonly ProductPromo[] | null
): SelectionPrice {
  return resolveSelectionPrice(
    undefined,
    { variations: product.variations, priceNormal: product.price_normal },
    // Fungsi ini memegang produknya, jadi aturan merek level (mig 7070)
    // diselesaikan di sini — kartu katalog tak perlu mengingatnya sendiri.
    levelForProduct(group, product),
    promos
  );
}

/**
 * Teks pil diskon untuk sebuah harga terpilih — "🔥 10% + 30%" (promo coret +
 * level), "🔥 -10%" (promo), "-30%" (level). Bila `cuts` kosong tapi harganya
 * lebih murah dari coretannya (pesanan lama tanpa jejak, harga warisan), jatuh
 * ke persen selisih `getDiscountInfo` seperti sebelum ada `cuts`. `null` =
 * tak ada yang perlu dipajang.
 */
export function discountBadgeLabel(
  view: Pick<SelectionPrice, "price" | "strikePrice" | "cuts" | "promo"> &
    Partial<Pick<SelectionPrice, "priceExtra">>,
  options: { flame?: boolean; compact?: boolean } = {}
): string | null {
  if (view.price === undefined) return null;
  const text = discountBadgeText(view.cuts, options);
  if (text) return text;
  // Persen selisih dinilai atas DASAR: tambahan (mig 7900) menempel utuh di
  // kedua angka, jadi ia dikeluarkan dulu supaya "-10%" tetap -10%.
  const extra = view.priceExtra ?? 0;
  const info = getDiscountInfo(
    view.strikePrice === undefined ? undefined : view.strikePrice - extra,
    view.price - extra
  );
  if (!info) return null;
  const flame = options.flame && view.promo ? "🔥 " : "";
  return `${flame}-${info.percent}%`;
}

/**
 * Harga tampilan produk (untuk listing / card).
 * Ambil dari varian pertama yang punya price_normal (promo & override-aware),
 * lalu fallback ke price_normal produk dengan diskon grup.
 */
export function getProductPrice(
  product: Product,
  group: CustomerGroup,
  promos?: readonly ProductPromo[] | null
): number | undefined {
  // Didelegasikan supaya tampilan & checkout mustahil menyimpang (lihat
  // catatan panjang di resolveSelectionPrice).
  const price = resolveSelectionPrice(
    undefined,
    { variations: product.variations, priceNormal: product.price_normal },
    levelForProduct(group, product),
    promos
  ).price;
  return price === undefined ? undefined : price + lazyFromPriceExtra(product);
}

/**
 * Bughunt D14-24 — mode malas (daftar varian dibuang): lapis akhir
 * `getProductPrice` = dasar termurah TANPA tambahan, angka "mulai dari" yang
 * tak bisa dibeli bila semua varian bertambahan. Ringkasan server yang
 * memastikan dasar SERAGAM (`minBase` = `maxBase` = `price_normal`) membuat
 * tambahan termurah pasti = total termurah − dasar; tambahan ditempel sesudah
 * potongan (D2). Dasar tak seragam / ringkasan tak ada = 0 (angka lama).
 * Khusus tampilan — `resolveSelectionPrice` (jalur uang) tak disentuh.
 */
function lazyFromPriceExtra(product: Product): number {
  if (product.variations.some((v) => v.price_normal !== undefined)) return 0;
  const range = product.variantPriceRange;
  if (!range || range.minBase === undefined || range.maxBase === undefined) return 0;
  if (range.minBase !== range.maxBase || range.minBase !== product.price_normal) return 0;
  const extra = range.min - range.minBase;
  return Number.isFinite(extra) && extra > 0 ? Math.round(extra) : 0;
}

/**
 * Harga yang BOLEH dibayar TAMU (tanpa level) untuk satu pilihan — angka yang
 * dikirim pixel iklan saat AddToCart (B7, PR-IG-4), apa pun level pembeli yang
 * sedang login. Harga level reseller/agen rahasia toko; sebelum ini AddToCart
 * mengirim harga yang dilihat reseller ke Meta/TikTok/GA4.
 *
 * Tak ada rumus baru: `resolveSelectionPrice(…, null, …)` + `sellableSelectionPrice`
 * atas katalog yang SAMA dengan yang dipajang halaman. `guestPromos` wajib dari
 * `guestPromosFor` (promo.ts) — jalur harga tamu feed Katalog Instagram &
 * JSON-LD (`productOffers.ts`). `undefined` = "harga menyusul".
 */
export function guestSellablePrice(
  selected: ProductVariation | undefined,
  catalog: Parameters<typeof resolveSelectionPrice>[1],
  guestPromos: readonly ProductPromo[]
): number | undefined {
  return sellableSelectionPrice(resolveSelectionPrice(selected, catalog, null, guestPromos)).price;
}

/**
 * Harga NORMAL produk untuk kartu katalog — pasangan `getProductPrice` yang
 * dipakai sebagai angka coret.
 *
 * JANGAN memakai `product.price_normal` untuk keperluan ini: ProductForm
 * menyimpannya sebagai MIN seluruh harga varian (ProductForm.tsx:924
 * `Math.min(...prices)`), sedangkan kartu menampilkan harga varian PERTAMA yang
 * ber-harga. Pada produk ber-harga varian campuran, mencoret `price_normal`
 * menampilkan "harga normal" yang LEBIH MURAH dari harga jualnya — coretan yang
 * terbaca seperti kenaikan harga.
 */
export function getProductNormalPrice(product: Product): number | undefined {
  // Didelegasikan ke resolver yang sama dengan `getProductPrice` supaya angka
  // yang DICORET dan angka yang DIPAJANG mustahil datang dari varian berbeda.
  // mig 7900: angka coret kartu = dasar + tambahan perwakilan (pasangan
  // `getProductPrice` yang juga memuat tambahan) — BUKAN `normalPrice` uang.
  const view = resolveSelectionPrice(undefined, {
    variations: product.variations,
    priceNormal: product.price_normal
  }, null);
  return view.normalPrice === undefined ? undefined : view.normalPrice + view.priceExtra;
}

/**
 * Rentang harga sekumpulan varian: "Rp 63.000" bila seragam, "Rp 63.000–Rp 90.000"
 * bila berbeda, "—" bila tak satu pun punya angka ("harga menyusul").
 *
 * Dipakai pratinjau dampak DAN layar pemilih produk di editor kampanye — satu
 * definisi supaya dua layar tak menuliskan rentang dengan aturan berbeda.
 */
export function formatPriceRange(prices: ReadonlyArray<number | undefined>): string {
  const nums = prices.filter((p): p is number => p !== undefined && Number.isFinite(p));
  if (!nums.length) return "—";
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  return min === max ? formatPrice(min) : `${formatPrice(min)}–${formatPrice(max)}`;
}

export function formatPrice(amount?: number): string {
  if (amount === undefined || amount === null) {
    return "Harga belum tersedia";
  }

  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0
  }).format(amount);
}

export function getPriceBadgeLabel(group: CustomerGroup): string | null {
  if (!group) return null;
  return `Harga ${group.name}`;
}

/**
 * Info diskon untuk badge "coret + persen": kembalikan harga normal & persen
 * potongan bila harga final lebih murah dari normal. Null bila tak ada diskon
 * (Customer biasa / harga sama / data tak lengkap).
 */
export function getDiscountInfo(
  normalPrice: number | undefined,
  finalPrice: number | undefined
): { normalPrice: number; percent: number } | null {
  if (
    normalPrice === undefined ||
    finalPrice === undefined ||
    normalPrice <= 0 ||
    finalPrice >= normalPrice
  ) {
    return null;
  }
  const percent = Math.round(((normalPrice - finalPrice) / normalPrice) * 100);
  if (percent <= 0) return null;
  return { normalPrice, percent };
}

/**
 * Label badge DP produk (uang muka). Null bila produk tidak wajib DP.
 * Maknanya mengikuti dpMode: percent (% harga) · per_pcs (Rp/pcs) ·
 * order/lainnya (Rp flat per order). Nilai DP ≤ 0 → badge generik "Wajib DP".
 */
export function getDpBadgeLabel(product: {
  dpRequired?: boolean;
  dpAmount?: number;
  dpMode?: "order" | "per_pcs" | "percent";
}): string | null {
  if (!product.dpRequired) return null;
  const amount = product.dpAmount;
  if (amount === undefined || amount <= 0) return "Wajib DP";
  switch (product.dpMode) {
    case "percent":
      return `DP ${amount}%`;
    case "per_pcs":
      return `DP ${formatPrice(amount)}/pcs`;
    default:
      return `DP ${formatPrice(amount)}`;
  }
}
